// src/components/HeroSlider.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const HeroSlider = () => {
  const [banners, setBanners] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const snap = await getDocs(collection(db, 'banners'));
        const allBanners = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const activeBanners = allBanners.filter(b => b.active !== false);
        activeBanners.sort((a, b) => (a.order || 99) - (b.order || 99));
        setBanners(activeBanners);
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners]);

  const nextSlide = () => setCurrentSlide((currentSlide + 1) % banners.length);
  const prevSlide = () => setCurrentSlide((currentSlide - 1 + banners.length) % banners.length);

  if (loading) {
    return (
      <div className="w-full h-64 md:h-96 bg-pink-50 animate-pulse flex items-center justify-center">
        <p className="text-pink-400">Loading...</p>
      </div>
    );
  }

  if (banners.length === 0) {
    return (
      <section className="py-10 md:py-20 px-4 md:px-6 text-center"
        style={{ background: 'linear-gradient(115deg, #FFF2F6, #FFFFFF, #FFF2F6)' }}>
        <h1 className="text-3xl md:text-6xl font-bold mb-3 md:mb-4 leading-tight"
          style={{ color: '#9B3F5B' }}>
          Fresh Organic Products
        </h1>
        <p className="text-sm md:text-lg mb-5 md:mb-8 max-w-2xl mx-auto px-2"
          style={{ color: '#DC829A' }}>
          Premium quality fresh organic products sourced directly from certified organic farms.
        </p>
        <Link to="/shop"
          className="inline-block px-6 md:px-8 py-3 rounded-lg hover:opacity-90 transition font-semibold text-sm md:text-base btn-2d"
          style={{ backgroundColor: '#F5AEC0', color: '#FFFFFF' }}>
          Shop Now
        </Link>
      </section>
    );
  }

  const banner = banners[currentSlide];

  return (
    <section className="relative w-full overflow-hidden bg-gray-100">
      <div className="relative w-full">
        <img
          src={banner.image}
          alt={banner.title || 'Banner'}
          className="w-full h-auto block"
        />

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: banner.overlayColor || '#000000',
            opacity: (banner.overlayOpacity !== undefined ? banner.overlayOpacity : 40) / 100,
          }}
        ></div>

        <div className="absolute inset-0 flex items-center">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <div className="max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl text-left">
              {banner.title && (
                <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-2 md:mb-4 leading-tight drop-shadow-lg">
                  {banner.title}
                </h1>
              )}

              {banner.description && (
                <p className="text-xs sm:text-sm md:text-base lg:text-lg text-white mb-3 md:mb-6 leading-relaxed drop-shadow-md">
                  {banner.description}
                </p>
              )}

              {banner.buttonText && banner.buttonLink && (
                <Link
                  to={banner.buttonLink}
                  className="inline-block px-4 sm:px-5 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition text-xs sm:text-sm md:text-base shadow-xl btn-2d"
                  style={{ backgroundColor: '#F5AEC0', color: '#FFFFFF' }}
                >
                  {banner.buttonText}
                </Link>
              )}
            </div>
          </div>
        </div>

        {banners.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white p-1.5 md:p-2.5 rounded-full transition z-10 backdrop-blur-sm"
            >
              <ChevronLeft size={18} className="md:w-6 md:h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white p-1.5 md:p-2.5 rounded-full transition z-10 backdrop-blur-sm"
            >
              <ChevronRight size={18} className="md:w-6 md:h-6" />
            </button>
          </>
        )}

        {banners.length > 1 && (
          <div className="absolute bottom-2 md:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 md:gap-2 z-10">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`rounded-full transition-all ${
                  i === currentSlide ? 'w-5 md:w-8 h-1.5 md:h-2' : 'w-1.5 md:w-2 h-1.5 md:h-2'
                }`}
                style={{ backgroundColor: i === currentSlide ? '#F5AEC0' : 'rgba(255,255,255,0.5)' }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSlider;